/**
 * The permission matcher — ONE reading of the wildcard rules, for every gate.
 *
 * ══════════════════════════════════════════════════════════════════════
 * ★ WHY THIS LIVES IN `@efacture/core` AND NOT NEXT TO ONE OF ITS CALLERS
 * ══════════════════════════════════════════════════════════════════════
 * There were two implementations of "does this user hold that permission",
 * and they disagreed:
 *
 *   - `packages/api/src/middleware/authorize.ts` expanded group wildcards,
 *     so a Gérant holding `dashboard:*` passed `dashboard:view`;
 *   - `packages/nlp/src/tools/registry.ts` used a flat `Array.includes`,
 *     so the SAME Gérant asking the chatbox « ventes du jour » was refused.
 *
 * Same grants, two answers, and the divergence was invisible because
 * neither side ever read the other. Tool ↔ route permission parity cannot
 * be asserted while the two sides do not even agree on what "holding" a
 * permission means, so the rule is stated once, here, and imported by
 * both. `@efacture/core` is the only package both the API and the NLP
 * package already depend on (Design Principle #1).
 *
 * ── THE RULES, AND WHAT EACH ONE IS FOR ───────────────────────────────
 *  1. `*` — the platform wildcard (ADMIN). Matches everything.
 *  2. Exact string match.
 *  3. Group wildcard: `invoices:*` matches `invoices:read`, `invoices:create`…
 *  4. A BROADER grant satisfies a NARROWER requirement: `invoices:read`
 *     satisfies a route that asks for `invoices:read:own`.
 *  0. Before all of them: a permission of `EXPLICIT_ONLY_PERMISSIONS` is
 *     satisfied by its own code only — neither `*`, a group wildcard nor a
 *     broader grant holds it.
 *
 * ⚠ RULE 4 DOES NOT RUN BACKWARDS, and that asymmetry is the point:
 * `invoices:read:own` does NOT satisfy `invoices:read`. A holder of the
 * `:own` variant may read THEIR OWN rows through a surface that filters by
 * owner; it must never open a surface that does not. The chatbox has no
 * owner-filtering surface, so its `:own` holders are refused — see
 * `ToolRegistry.checkPermissions`.
 */
/**
 * Permissions only an explicit grant of the same code holds.
 * `mcf-agent:enroll` mints the credential of a site MCF agent, which then
 * certifies invoices in the tenant's name: a role gets it by naming it,
 * never through `*` (a custom role given every permission included).
 * `mcf:gerer-appareils` registers and retires the MCF devices an outage is
 * declared against: neither `*` nor `mcf:*` holds it.
 * `platform:certification-currency` sets the currency every tenant of the
 * jurisdiction certifies in, and `platform:fiscal-parameters` its dated
 * fiscal parameters; `platform:taxpayer-category` validates a
 * tenant's declaration that exempts it from certification;
 * `platform:jurisdiction` moves a company out of a jurisdiction with a
 * certifier; `platform:no-mcf-transitional` closes the transitional « pas
 * de MCF » issuing mode; `platform:certification-obligation` dates and
 * suspends the certification obligation of the jurisdiction;
 * `platform:dgi-incidents` declares and ends the DGI incidents under which
 * subject tenants issue without the MCF; `platform:sfe-fleet` reads every
 * tenant's installs by SFE version. All eight also require the platform
 * token.
 * `pos:price-override` sets a till line's price instead of the catalogue's:
 * a cashier role composed with `pos:*` does not acquire it.
 * `cabinet:access` opens the accounting-firm portal: an administrator of a
 * company does not reach it through `*`, only the firm's staff role names it.
 * `invoices:reissue-original` emits an invoice's original a second time
 * (rule C): neither `*` nor `invoices:*` holds it.
 */
export const EXPLICIT_ONLY_PERMISSIONS = new Set([
    "cabinet:access",
    "invoices:reissue-original",
    "mcf-agent:enroll",
    "mcf:gerer-appareils",
    "platform:certification-currency",
    "platform:certification-obligation",
    "platform:dgi-incidents",
    "platform:sfe-fleet",
    "platform:fiscal-parameters",
    "platform:no-mcf-transitional",
    "platform:taxpayer-category",
    "platform:jurisdiction",
    "pos:price-override",
]);
/**
 * Does one held permission satisfy one required permission?
 *
 * @param userPermission - a permission string the user holds
 * @param requiredPermission - the permission the surface requires
 */
export function matchesPermission(userPermission, requiredPermission) {
    // 0. Explicit-only permissions: the same code, nothing else.
    if (EXPLICIT_ONLY_PERMISSIONS.has(requiredPermission)) {
        return userPermission === requiredPermission;
    }
    // 1. Full wildcard matches everything.
    if (userPermission === "*") {
        return true;
    }
    // 2. Exact match.
    if (userPermission === requiredPermission) {
        return true;
    }
    // 3. Group wildcard (e.g., "invoices:*" matches "invoices:create").
    if (userPermission.endsWith(":*")) {
        const prefix = userPermission.slice(0, -1); // "invoices:"
        if (requiredPermission.startsWith(prefix)) {
            return true;
        }
    }
    // 4. Broader permission satisfies narrower
    //    (e.g., "invoices:read" satisfies "invoices:read:own").
    if (requiredPermission.startsWith(userPermission + ":")) {
        return true;
    }
    return false;
}
/** Does the user hold a permission that satisfies `requiredPermission`? */
export function hasPermission(userPermissions, requiredPermission) {
    return userPermissions.some((userPerm) => matchesPermission(userPerm, requiredPermission));
}
/** AND logic — every required permission must be satisfied. */
export function hasAllPermissions(userPermissions, requiredPermissions) {
    return requiredPermissions.every((required) => hasPermission(userPermissions, required));
}
/**
 * The required permissions the user does NOT hold, in declaration order.
 * Used by the surfaces that name what is missing in their refusal.
 */
export function missingPermissions(userPermissions, requiredPermissions) {
    return requiredPermissions.filter((required) => !hasPermission(userPermissions, required));
}
//# sourceMappingURL=permission-matcher.js.map